import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type JointPaletteDb = {
  name: string;
  jointDateTime: string;
  message: string;
}
const initial: JointPaletteDb = {
  name: '',
  jointDateTime: '',
  message: ''
};

export type JointPaletteDbHashType = {
  [key in string]: JointPaletteDb;
}

export type JointPaletteAddPayload = {
  id?: string;
} & JointPaletteDb;
export type JointPaletteUpdatePayload = {
  id: string;
} & { [P in keyof JointPaletteDb]?: JointPaletteDb[P] };

export type JointPaletteDbState = {
  jointPalettes: JointPaletteDbHashType;
}

export const generateJointPaletteId = (): string => generateId('jp');
let initialState: JointPaletteDbState = { jointPalettes: {} };
const key = 'jointPalettes';

const jointPaletteDbSlice = createSlice({
  name: 'jointPaletteDb',
  initialState,
  reducers: {
    initializeJointPalettes(state: JointPaletteDbState) {
      state.jointPalettes = {};
    },
    addJointPalette(state: JointPaletteDbState, action: PayloadAction<JointPaletteAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateJointPaletteId();
      state.jointPalettes[id] = obj;
      saveData(key, id, obj);
    },
    updateJointPalette(state: JointPaletteDbState, action: PayloadAction<JointPaletteUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.jointPalettes[id] === undefined) {
        console.log(`*** JointPalette not found: ${id}`);
      } else {
        const newData = { ...state.jointPalettes[id], ...obj };
        state.jointPalettes[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteJointPalette(state: JointPaletteDbState, action: PayloadAction<JointPaletteUpdatePayload>) {
      const id = action.payload.id;
      delete state.jointPalettes[id];
      deleteData(key, id);
    },
    saveJointPalettes(state: JointPaletteDbState) {
      saveAllData(key, state.jointPalettes);
    },
    signUpJointPalettes(state: JointPaletteDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.jointPalettes, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: JointPaletteDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.jointPalettes = updatedValues;
      if (changed) saveAllData(key, state.jointPalettes);
    })
  }
})

export const { initializeJointPalettes, addJointPalette, updateJointPalette, deleteJointPalette, saveJointPalettes, signUpJointPalettes } = jointPaletteDbSlice.actions;
export default jointPaletteDbSlice.reducer;
